import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const JobSeekerSearchByPosition = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [jobSeekers, setJobSeekers] = useState([]);
  const [filteredSeekers, setFilteredSeekers] = useState([]);
  const [selectedSeeker, setSelectedSeeker] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch job seekers on component mount
  useEffect(() => {
    const fetchJobSeekers = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}jobseeker/allJobSeeker`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        // Validate response data
        if (Array.isArray(response.data)) {
          setJobSeekers(response.data);
          setFilteredSeekers(response.data);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } catch (error) {
        console.error('Error fetching job seekers:', error);
        toast.error(error.response?.data?.message || 'Failed to fetch job seekers');
      } finally {
        setIsLoading(false);
      }
    };
    fetchJobSeekers();
  }, []);

  // Filter job seekers based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredSeekers(jobSeekers);
      return;
    }

    const filtered = jobSeekers.filter((seeker) => {
      // Ensure interestedJob is an array and handle undefined/null cases
      if (!seeker?.interestedJob || !Array.isArray(seeker.interestedJob)) {
        return false;
      }
      return seeker.interestedJob.some((job) =>
        job?.toLowerCase().includes(searchTerm.toLowerCase().trim())
      );
    });
    setFilteredSeekers(filtered);
  }, [searchTerm, jobSeekers]);

  // Open modal with selected seeker's details
  const openModal = (seeker) => {
    setSelectedSeeker(seeker);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSeeker(null);
    setMessage('');
  };

  // Send interview message
  const sendInterviewMessage = async (seekerId, email) => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}messages/send`,
        {
          jobSeekerId: seekerId,
          message,
          email,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setSentInvitations([...sentInvitations, { id: seekerId, email, message }]);
      toast.success(response.data.message || 'Interview invitation sent successfully');
      closeModal();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.message || 'Failed to send invitation');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Search Input */}
      <div className="mb-6">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search job seekers by interested job..."
          className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center my-8">
          <svg
            className="animate-spin h-8 w-8 text-blue-500"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7. Vector3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}

      {/* Sent Invitations Section */}
      {sentInvitations.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Sent Invitations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sentInvitations.map((invitation, index) => (
              <div key={index} className="p-4 border rounded-md bg-gray-50">
                <p><strong>Email:</strong> {invitation.email}</p>
                <p><strong>Message:</strong> {invitation.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Job Seekers List */}
      {!isLoading && filteredSeekers.length === 0 && (
        <p className="text-center text-gray-500 my-8">No job seekers found.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSeekers.map((seeker) => (
          <div key={seeker._id} className="p-4 border rounded-md shadow-sm hover:shadow-md transition">
            <h3 className="text-lg font-semibold">{seeker.firstName} {seeker.lastName}</h3>
            <p><strong>Email:</strong> {seeker.email}</p>
            <p><strong>Interested Jobs:</strong> {seeker.interestedJob?.join(', ') || 'None'}</p>
            <button
              onClick={() => openModal(seeker)}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
            >
              See More
            </button>
          </div>
        ))}
      </div>

      {/* Modal for Job Seeker Details */}
      {isModalOpen && selectedSeeker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-md max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{selectedSeeker.firstName} {selectedSeeker.lastName}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p><strong>Email:</strong> {selectedSeeker.email || 'N/A'}</p>
              <p><strong>Phone:</strong> {selectedSeeker.phoneNumber || 'N/A'}</p>
              <p><strong>Address:</strong> {selectedSeeker.address || 'N/A'}</p>
              <p><strong>State:</strong> {selectedSeeker.state || 'N/A'}</p>
              <p><strong>Country:</strong> {selectedSeeker.country || 'N/A'}</p>
              <p><strong>Gender:</strong> {selectedSeeker.gender || 'N/A'}</p>
              <p><strong>Job Mode:</strong> {selectedSeeker.jobMode || 'N/A'}</p>
              <p><strong>Job Decision:</strong> {selectedSeeker.jobDecision || 'N/A'}</p>
              <p><strong>Interested Jobs:</strong> {selectedSeeker.interestedJob?.join(', ') || 'N/A'}</p>
              <p><strong>Education Level:</strong> {selectedSeeker.educationLevel || 'N/A'}</p>
              <p><strong>Marital Status:</strong> {selectedSeeker.maritalStatus || 'N/A'}</p>
              <p><strong>Minimum Salary:</strong> {selectedSeeker.minimumSalary || 'N/A'}</p>
              <p><strong>Skills:</strong> {selectedSeeker.handworkSkill || 'N/A'}</p>
              <p>
                <strong>Years of Experience:</strong>{' '}
                {selectedSeeker.servicesRendered?.[0]?.yearsExperience || 'N/A'}
              </p>
            </div>

            {/* Message Input */}
            <div className="mt-6">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your interview invitation message..."
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
              />
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  onClick={() => sendInterviewMessage(selectedSeeker._id, selectedSeeker.email)}
                  disabled={isSending}
                  className={`px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center transition ${
                    isSending ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSending && (
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                  Send Message
                </button>
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSeekerSearchByPosition;